import { castDraft, create } from "mutative";
import { useMemo, useReducer, type PropsWithChildren } from "react";

import type { Permission } from "src/api/Tmd.dtos";
import {
    defaultLayoutContext,
    LayoutActionsContext,
    LayoutContext,
    LayoutPermissionMode,
    PermissionScopeMode,
    type ILayoutActionsContext,
    type ILayoutBreadcrumb,
    type ILayoutContext,
    type ILayoutItemContext,
    type ILayoutLink,
    type ILayoutSubMenu
} from "src/contexts/layouts/LayoutContext";

/**
 * Describes the supported layout updates, including owner-scoped breadcrumb replacement.
 */
type LayoutAction =
    | { type: "setMenuItems"; menuItems: ILayoutLink[] }
    | { type: "setSubMenus"; subMenus: ILayoutSubMenu[] }
    | { type: "addSubMenu"; subMenu: ILayoutSubMenu }
    | { type: "removeSubMenu"; subMenu: ILayoutSubMenu }
    | { type: "addSubMenuByCode"; code: string }
    | { type: "removeSubMenuByCode"; code: string }
    | { type: "setRestricted"; restricted: boolean }
    | { type: "setPermission"; permission: Permission }
    | { type: "setPermissionMode"; permissionMode: LayoutPermissionMode }
    | { type: "setPermissionScopeMode"; permissionScopeMode: PermissionScopeMode }
    | { type: "setLoading"; loading: boolean }
    | { type: "setContext"; context: ILayoutItemContext }
    | { type: "setPageBreadcrumbs"; owner: string; breadcrumbs: ILayoutBreadcrumb[] }
    | { type: "removePageBreadcrumbs"; owner: string };

/**
 * Produces the layout state for one action without mutating the input state.
 *
 * Postconditions: Adding a submenu object replaces the first code match or appends; adding only
 * a code leaves an existing match unchanged. Removal deletes every matching code.
 * Breadcrumb updates remove the owner's prefixed entries before appending replacements with that
 * prefix. Unrelated entries retain their order. Context updates replace the complete context.
 */
export function LayoutReducer(state: ILayoutContext, action: LayoutAction): ILayoutContext {
    return create(
        state,
        draft => {
            switch (action.type) {
                case "setMenuItems":
                    draft.menuItems = castDraft(action.menuItems);
                    break;

                case "setSubMenus":
                    draft.subMenus = castDraft(action.subMenus);
                    break;

                case "addSubMenu": {
                    const subMenus = draft.subMenus ?? [];
                    const existingIndex = subMenus.findIndex(subMenu =>
                        subMenu.code === action.subMenu.code
                    );

                    if (existingIndex >= 0) {
                        subMenus[existingIndex] = castDraft(action.subMenu);
                    } else {
                        subMenus.push(castDraft(action.subMenu));
                    }

                    draft.subMenus = subMenus;
                    break;
                }

                case "removeSubMenu":
                    draft.subMenus = (draft.subMenus ?? []).filter(subMenu =>
                        subMenu.code !== action.subMenu.code
                    );
                    break;

                case "addSubMenuByCode":
                    if (!(draft.subMenus ?? []).some(subMenu => subMenu.code === action.code)) {
                        draft.subMenus = [
                            ...(draft.subMenus ?? []),
                            {
                                code: action.code,
                                items: []
                            }
                        ];
                    }
                    break;

                case "removeSubMenuByCode":
                    draft.subMenus = (draft.subMenus ?? []).filter(subMenu =>
                        subMenu.code !== action.code
                    );
                    break;

                case "setRestricted":
                    draft.restricted = action.restricted;
                    break;

                case "setPermission":
                    draft.permission = action.permission;
                    break;

                case "setPermissionMode":
                    draft.permissionMode = action.permissionMode;
                    break;

                case "setPermissionScopeMode":
                    draft.permissionScopeMode = action.permissionScopeMode;
                    break;

                case "setLoading":
                    draft.loading = action.loading;
                    break;

                case "setContext":
                    draft.context = action.context;
                    break;

                case "setPageBreadcrumbs":
                    draft.breadcrumbs = castDraft([
                        ...(draft.breadcrumbs ?? []).filter(breadcrumb =>
                            !breadcrumb.code.startsWith(`${action.owner}:`)
                        ),
                        ...action.breadcrumbs.map(breadcrumb => ({
                            ...breadcrumb,
                            code: `${action.owner}:${breadcrumb.code}`
                        }))
                    ]);
                    break;

                case "removePageBreadcrumbs":
                    draft.breadcrumbs = (draft.breadcrumbs ?? []).filter(breadcrumb =>
                        !breadcrumb.code.startsWith(`${action.owner}:`)
                    );
                    break;
            }
        }
    );
}

/**
 * Supplies descendants and an optional initial layout snapshot.
 */
interface ILayoutProviderProps extends PropsWithChildren {
    initialState?: ILayoutContext;
}

/**
 * Owns layout state and exposes stable dispatch adapters through a separate action context.
 *
 * Postconditions: Actions schedule reducer updates; callers observe changes on later renders.
 * Assumptions: initialState initializes the reducer and is not a synchronization mechanism for
 * subsequent prop changes.
 */
function LayoutProvider({ children, initialState }: ILayoutProviderProps) {
    const [layout, dispatch] = useReducer(LayoutReducer, initialState ?? defaultLayoutContext);

    const actions = useMemo<ILayoutActionsContext>(
        () => ({
            setMenuItems: (menuItems: ILayoutLink[]) =>
                dispatch({ type: "setMenuItems", menuItems }),
            setSubMenus: (subMenus: ILayoutSubMenu[]) =>
                dispatch({ type: "setSubMenus", subMenus }),
            addSubMenu: (subMenu: ILayoutSubMenu) => dispatch({ type: "addSubMenu", subMenu }),
            removeSubMenu: (subMenu: ILayoutSubMenu) =>
                dispatch({ type: "removeSubMenu", subMenu }),
            addSubMenuByCode: (code: string) => dispatch({ type: "addSubMenuByCode", code }),
            removeSubMenuByCode: (code: string) => dispatch({ type: "removeSubMenuByCode", code }),
            setRestricted: (restricted: boolean) => dispatch({ type: "setRestricted", restricted }),
            setPermission: (permission: Permission) =>
                dispatch({ type: "setPermission", permission }),
            setPermissionMode: (permissionMode: LayoutPermissionMode) =>
                dispatch({ type: "setPermissionMode", permissionMode }),
            setPermissionScopeMode: (permissionScopeMode: PermissionScopeMode) =>
                dispatch({ type: "setPermissionScopeMode", permissionScopeMode }),
            setLoading: (loading: boolean) => dispatch({ type: "setLoading", loading }),
            setContext: (context: ILayoutItemContext) => dispatch({ type: "setContext", context }),
            setPageBreadcrumbs: (owner: string, breadcrumbs: ILayoutBreadcrumb[]) =>
                dispatch({ type: "setPageBreadcrumbs", owner, breadcrumbs }),
            removePageBreadcrumbs: (owner: string) =>
                dispatch({ type: "removePageBreadcrumbs", owner })
        }),
        [dispatch]
    );

    return (
        <LayoutContext.Provider value={layout}>
            <LayoutActionsContext.Provider value={actions}>
                {children}
            </LayoutActionsContext.Provider>
        </LayoutContext.Provider>
    );
}

export default LayoutProvider;
